import React, { useContext } from 'react';
import Img from 'gatsby-image';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import SEO from '../components/SEO';
import localize from '../components/localize';
import LocaleBlock from '../components/LocaleBlock';
import Legend from '../components/Legend';
import Gallery from '../components/Gallery';
import Vimeo from '../components/Vimeo';
import Hideaway from '../components/Hideaway';
import Footer from '../components/Footer';
import Collapsible from '../components/Collapsible';
import { tr } from '../utils/translations';
import LocaleContext from '../components/LocaleContext';
import { breakpoints } from '../styles/Variables';
import { PageStyles } from '../styles/PageStyles';
import PropertiesCarousel from '../components/PropertiesCarousel';

const PropertyPageStyles = styled.div`
  .hero {
    max-height: 40vh;
    margin-bottom: 4rem;
  }

  .preHeader {
    text-transform: uppercase;
    text-align: center;
    margin-bottom: 2rem;
  }

  .pageTitle {
    text-align: center;
    font-size: var(--fsTitle1);
    padding: 0 1rem;
  }

  .hideaway {
    width: 100%;
  }

  blockquote {
    font-size: var(--fsQuote);
    text-align: center;
    margin: 0;
    display: block;
    padding: 1rem 0;

    @media (min-width: ${breakpoints.breakLg}) {
      padding: 0 4rem;
      margin-bottom: 3rem;
      /* display: flex;
      align-items: center;
      justify-content: center; */
    }
  }

  &:not(.lessQuotePadding) {
    blockquote {
      padding: 3rem 0;
    }
  }

  .collapsible {
    margin-bottom: 2rem;

    p {
      margin-bottom: 1em;
    }

    &.rooms .title {
      border-top: 1px solid var(--black);
      padding-top: 1.25rem;
    }

    /* @media (min-width: ${breakpoints.breakLg}) {
      width: 50%;
      display: inline-block;
      vertical-align: top;
      margin-bottom: 4rem;
      margin-top: 1rem;

      &.rooms {
        padding-left: var(--col1);
        padding-right: 1rem !important;
      }

      &.amenities {
        padding-left: 1rem !important;
        padding-right: var(--col1);
      }
    } */
  }

  .buttonWrapper {
    .cta {
      color: var(--black);
      border-color: transparent;
      background-color: var(--orange);
      margin-top: 2rem;
      margin-bottom: 2rem;
      font-size: 1.25em;

      &:hover {
        background-color: var(--offWhite);
        border-color: var(--black);
      }
    }

    margin-bottom: 2rem;
  }

  .sections {
    .intro {
      margin-bottom: 3rem;
      text-align: justify;

      p {
        // This span should have a class of "number", but not working yet
        span {
          font-family: Avenir, sans-serif;
        }
      }

      h3 {
        text-align: center;
      }

      &.withNarrowImage {
        margin-bottom: 2rem;

        .gatsby-image-wrapper {
          width: var(--col8);
          margin: 4rem auto 2rem auto;
        }
      }
    }

    .collapsible {
      padding: 0 var(--col1);
    }

    &.rent > :first-child {
      // No margin after intro on rental
      margin-bottom: 0;
    }
  }

  .faqBlock {
    h1,
    h2,
    h3 {
      text-transform: none;
    }

    p {
      padding-left: 0;
      padding-right: 0;
    }

    .gatsby-image-wrapper {
      margin-bottom: 2rem;
    }
  }

  &.liveWork {
    padding-top: 4rem;
  }

  &.buy {
    .sections > .gallery:not(.mb) {
      padding: 0;
      margin-bottom: 0;
    }
  }

  @media (min-width: ${breakpoints.breakLg}) {
    &:not(.casaMusa) {
      &.hasHeroVideo {
        padding-top: 4rem;
      }

      .heroVideoWrapper {
        max-width: var(--maxPageWidth);
        margin: 0 auto;
        padding-left: var(--col1);
        padding-right: var(--col1);
      }
    }

    .sections {
      max-width: unset;

      &.rent > :first-child,
      &.rent > .block:first-child {
        // No margin after intro on rental
        margin-bottom: 0;
      }

      .block {
        margin-bottom: 3rem;
      }

      .intro {
        max-width: var(--maxPageWidth);
        margin-left: auto;
        margin-right: auto;

        .wrapper {
          margin-bottom: 2rem;
        }
      }

      &.buy {
        .gallery {
          .slick-slide {
            max-height: 100vh;
          }

          .toggleExpanded,
          .slick-arrow {
            width: 3rem;
            height: 3rem;
          }
        }
      }
    }

    // Align faqBlocks vertically to top instead of center
    .faqBlock.imageLeft .wrapper {
      grid-template-rows: unset;
      align-items: flex-start;
      grid-template-columns: var(--col6) var(--col6);
    }

    &.customBuild {
      .sections {
        .intro .wrapper {
          display: grid;
          grid-template-columns: var(--col7) 1fr 1fr;

          & > :first-child {
            padding-right: 3rem;
          }
        }
      }
    }

    &.buy .intro {
      margin-bottom: 6rem;
    }

    &.narrowSections {
      .sections {
        padding: 0 var(--col1);

        .gallery {
          margin-bottom: 4rem;
        }

        .block:not(.faqBlock) {
          p {
            padding-left: 3rem;
            padding-right: 3rem;
          }
        }

        &.buy {
          // Presuming we want a more horizontal look for all narrow-style property pages
          .gallery .slick-slide {
            max-height: 65vh;
          }
        }
      }
    }
  }
`;

const PropertyPage = ({ data, pageContext }) => {
  const [locale] = useContext(LocaleContext);
  const { page, allPages } = data;
  const thisPageType = page.type;
  const thisPageSlug = page.slug;
  const siteSettings = data.siteSettings.nodes[0];
  const pageClassName = `${page.className} ${page.type} ${
    page.heroVideoVimeoID ? 'hasHeroVideo' : ''
  }`;

  return (
    <>
      <SEO title={page.metaname} description={page.metaDescription} />
      {page && (
        <PageStyles className="propertyPage">
          <PropertyPageStyles className={pageClassName}>
            {/* ==== Hero section ==== */}
            {page.heroVideoVimeoID && (
              <div className="heroVideoWrapper">
                <Vimeo
                  className="hero video"
                  vimeoID={page.heroVideoVimeoID}
                  heightRatio={page.heroVideoRatioPercent || 50}
                  controls
                />
              </div>
            )}
            {page.heroImage && (
              <Img
                fluid={page.heroImage.asset.fluid}
                alt={page.name}
                className="hero"
              />
            )}
            {/* ==== End hero section ==== */}

            {/* Add "phase 2" for Live-Work page */}
            {page.className === 'live-work' && (
              <p className="preHeader">{tr('titles', 'phase', locale)} 2</p>
            )}
            {/* Main property title */}
            <h1 className="pageTitle">{page.name}</h1>

            {/* Tabs (unused 2021-07-26) */}
            {/* <div className="allPages desktop" role="navigation">
              <div className="linksWrapper">
                {allPages.nodes.map((aPage) => {
                  // List other properties of same type
                  if (thisPageType !== aPage.type) return null;
                  return (
                    <LocaleLink to={`/${aPage.slug}`} key={aPage.slug}>
                      <h2>{aPage.name}</h2>
                    </LocaleLink>
                  );
                })}
              </div>
            </div> */}

            {/* ==== Page body ==== */}
            <div className={`sections ${thisPageType}`}>
              {page._rawSections &&
                page._rawSections.map((section, index) => {
                  if (section._type === 'link') {
                    return (
                      <div className="buttonWrapper" key={section.url}>
                        <a
                          href={section.url}
                          className={`button cta ${section.className}`}
                        >
                          {section.text}
                        </a>
                      </div>
                    );
                  }

                  if (section._type === 'gallery') {
                    return (
                      <div
                        className={`gallery ${section.className}`}
                        key={section._key}
                      >
                        <Gallery
                          imageMaxWidth={pageContext.imageMaxWidth}
                          images={section.images}
                          className={section.className}
                        />
                      </div>
                    );
                  }
                  if (section._type === 'hideaway') {
                    return (
                      <Hideaway
                        className={`hideaway ${section.className}`}
                        key={section._key}
                        name={section.name}
                        gallery={section.gallery}
                        legends={section.legends}
                        imageMaxWidth={pageContext.imageMaxWidth}
                      />
                    );
                  }
                  if (section._type === 'legend') {
                    return (
                      <Legend
                        key={section._key}
                        name={section.name}
                        text={section.text}
                        className={section.className}
                      />
                    );
                  }
                  if (section._type === 'imageWithAlt') {
                    return (
                      <LocaleBlock key={section._key} content={[section]} />
                    );
                    // return <Img fluid={section.asset.fluid} alt={section.alt} />;
                  }
                  if (section._type === 'collapsible') {
                    return (
                      <Collapsible
                        key={section._key}
                        title={section.name}
                        text={section.text}
                        className={section.className}
                        defaultCollapsed
                      />
                    );
                  }
                  if (section._type === 'vimeo') {
                    return (
                      <Vimeo
                        key={section._key}
                        vimeoID={section.vimeoID}
                        heightRatio={section.heightRatio}
                        controls
                        allowExpand
                      />
                    );
                  }
                  if (Array.isArray(section)) {
                    return (
                      <LocaleBlock
                        className={section.className}
                        content={section}
                        key={`${section._key}_${index}`}
                      />
                    );
                  }

                  // Unknown section type (this shouldn't happen) – return empty
                  return <></>;
                })}
            </div>
            {/* ==== End page body ==== */}

            <PropertiesCarousel
              pages={allPages}
              showPageType={thisPageType}
              parentPageSlug={thisPageSlug}
              className="allPages"
              // Roll back Stay Now? thisPageType === 'buy' ? 'viewMoreProperties' : 'stayNow',
              title={tr('titles', 'viewMoreProperties', locale)}
            />
          </PropertyPageStyles>
        </PageStyles>
      )}
      {siteSettings && <Footer siteSettings={siteSettings} />}
    </>
  );
};
export default localize(PropertyPage);

export const query = graphql`
  query($pageId: String!, $imageMaxWidth: Int!) {
    siteSettings: allSanitySiteSettings {
      nodes {
        ...SiteSettingsFields
      }
    }
    allPages: allSanityPropertyType(
      filter: { hidden: { ne: true } }
      sort: { fields: order }
    ) {
      nodes {
        id
        type
        name {
          ...LocaleStringFields
        }
        slug {
          ...LocaleStringFields
        }
        _rawIntroduction
        image {
          asset {
            fluid(maxWidth: 800) {
              ...GatsbySanityImageFluid
            }
          }
        }
      }
    }
    page: sanityPropertyType(id: { eq: $pageId }) {
      id
      type
      className
      slug {
        ...LocaleStringFields
      }
      name {
        ...LocaleStringFields
      }
      metaname {
        ...LocaleStringFields
      }
      metaDescription {
        ...LocaleStringFields
      }
      image {
        asset {
          fluid(maxWidth: $imageMaxWidth) {
            ...GatsbySanityImageFluid
          }
        }
      }
      heroImage {
        asset {
          fluid(maxWidth: $imageMaxWidth) {
            ...GatsbySanityImageFluid
          }
        }
      }
      heroVideoVimeoID
      heroVideoRatioPercent
      _rawSections
    }
  }
`;
